import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Table from '../../../../../../_common/Tables/Table';
import {
  Card, LineChart, SectionHead, StatsCards,
} from '../../PipelineComponents';
import { formattedCount, formattedPercent } from '../../../pipelineHelpers';

const cohortsTableHeader = [
  { name: 'Cohort', path: 'cohort' },
  { name: 'Members', path: 'members' },
  { name: 'Month 1', path: 'month1' },
  { name: 'Month 3', path: 'month3' },
  { name: 'Month 6', path: 'month6' },
  { name: 'Month 12', path: 'month12' },
];

const retainedPercent = (value) => (value ? formattedPercent(value) : '-');

const netGrowth = (value) => (value > 0 ? `+${formattedCount(value)}` : formattedCount(value));

const CHURN_REASON_TIPS = {
  'No Longer Interested': {
    text: 'Members who decided not to continue.',
    steps: ['Recorded when the member says so on a call.'],
    note: 'Usually the largest recorded reason and the hardest to change. Worth asking what happened in the'
      + ' weeks before it was recorded - the decision is normally made earlier than the note.',
  },
  'Unable to Follow Up': {
    text: 'Members who could not be reached.',
    steps: ['Recorded when outreach runs out of options.'],
    note: 'A process outcome rather than a member decision, which makes it the most winnable of the reasons'
      + ' here.',
  },
  'Unable to Transfer': {
    text: 'Members whose transfer could not be completed.',
    steps: ['Recorded when the transfer is closed without a fill.'],
    note: 'Often small, but section 09 shows transfers are not being tracked end to end yet - this number'
      + ' will grow once they are.',
  },
};

const Retention = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getRetentionSummary(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params.hub]);

  const {
    cards, cohorts, churnReasons, churnTotals,
  } = useMemo(() => {
    const kpis = summary?.kpis;

    return {
      cards: [
        {
          label: 'Active members',
          value: formattedCount(kpis?.activeMembers),
          tooltip: {
            text: 'Members who have filled with you recently.',
            steps: ['This section always looks at the last twelve months, whatever the date filter says.'],
            note: 'The date range at the top of the page does not change anything here — retention needs a'
              + ' long window to mean anything.',
          },
        },
        {
          label: '3-month retention',
          value: formattedPercent(kpis?.retention3m),
          tooltip: {
            text: 'The share of members still filling three months after they started.',
            steps: ['Measured against everyone who started at least three months ago.'],
            note: 'The earliest reliable signal. Members who make it past three months usually stay'
              + ' considerably longer.',
          },
        },
        {
          label: '6-month retention',
          value: formattedPercent(kpis?.retention6m),
          tooltip: {
            text: 'The share still filling after six months.',
            steps: ['Measured against everyone who started at least six months ago.'],
            note: 'If this comes out higher than the three-month figure beside it, the two are being measured'
              + ' over different cohorts — retention normally falls as the window lengthens.',
            warn: true,
          },
        },
        {
          label: '12-month retention',
          value: formattedPercent(kpis?.retention12m),
          tooltip: {
            text: 'The share still filling after a full year.',
            steps: ['Measured against everyone who started at least a year ago.'],
            note: 'Check it against the cohort table below. Zero here while the table still shows members'
              + ' filling at month 12 means the KPI is not picking that cohort up.',
            warn: true,
          },
        },
        {
          label: 'Churned (30d)',
          value: formattedCount(kpis?.churned30d),
          tooltip: {
            text: 'Members who stopped filling in the last 30 days.',
            steps: ['Filled recently enough to be counted active, then nothing in the last 30 days.'],
            note: 'The only group here you can still act on. After 60 days members are much harder to bring'
              + ' back.',
          },
        },
        {
          label: 'Net member growth',
          value: netGrowth(kpis?.netGrowth),
          tooltip: {
            text: 'New members minus churned members.',
            steps: ['Measured across the same period.'],
            note: 'A negative figure means more members left than joined, which makes the churn breakdown'
              + ' below the most important panel on the page.',
          },
        },
      ],

      cohorts: (summary?.cohorts || []).map((d) => ({
        ...d,
        cohort: moment(d.cohort).format('MMM YYYY'),
        members: formattedCount(d.members),
        month1: retainedPercent(d.month1),
        month3: retainedPercent(d.month3),
        month6: retainedPercent(d.month6),
        month12: retainedPercent(d.month12),
      })),

      churnReasons: (summary?.churnReasons || []).map((d) => ({
        label: d.lostReason,
        value: +(d.count || 0),
        caption: formattedCount(d.count),
        tooltip: CHURN_REASON_TIPS[d.lostReason],
      })),

      churnTotals: [
        {
          label: 'Churned with an open pipeline record',
          value: formattedCount(summary?.churnedWithPipeline),
        },
        {
          label: 'Churned with no outreach logged',
          value: formattedCount(summary?.churnedNoOutreach),
        },
      ],
    };
  }, [summary]);

  return (
    <>
      <SectionHead section={section} />

      <StatsCards
        cards={cards}
        loading={loading}
        firstLoad={firstLoad.current}
      />

      <div className={`${classes.row} ${classes.row_2u}`}>
        <Card
          title="Cohort retention"
          caption="Share of each starting cohort still filling"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!cohorts.length}
        >
          <div className={`${classes.pipeline_table} ${classes.bordered}`}>
            <Table
              header={cohortsTableHeader}
              data={cohorts}
            />
          </div>

          <p className={classes.note}>
            <b>Retained</b>
            {' '}
            = at least one fill in the month, counted from the member&apos;s first fill. Members
            converted to a 90-day supply retain better than the
            {' '}
            {formattedPercent(summary?.kpis?.retention6m)}
            {' '}
            book average at six months - the clearest retention lever on this page.
          </p>
        </Card>

        <Card
          title="Why members leave"
          caption={`${formattedCount(summary?.kpis?.churned30d)} churned members · last 30 days`}
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!churnReasons.length}
        >
          <LineChart
            data={churnReasons}
            className={classes.bordered}
          />

          <div className={classes.kv_list}>
            {churnTotals.map((item) => (
              <div className={classes.kv} key={item.label}>
                {item.label}

                <span className={classes.kv_value}>{item.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* <RetentionMonthlyCard params={params} /> */}
    </>
  );
};

Retention.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default Retention;
